import * as React from "react";
import { useState, useEffect } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";

// UI
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileUpload } from "@/components/FileUpload";
import { useToast } from "@/components/ui/use-toast";
import { Spinner } from "@/components/ui/spinner";

// Icons
// import { CirclePlus } from "lucide-react";

// Services and Utils
import { getDebt, editDebt } from "@/services/finance";
// import { formatNumber } from "@/utils/formatNumber";

export const Route = createFileRoute(
    "/_layout-private/report/finance/company/_layout/finance-debt/edit"
)({
    component: RouteComponent,
});

function RouteComponent() {
    // Instance and Constants
    const { toast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const companyId = localStorage.getItem("companyId"); // Get companyId from local storage
    const yearly = Number(localStorage.getItem("year")); // Get year from local storage

    // States
    const [docFile, setDocFile] = useState("");
    const [revolvingDebts, setRevolvingDebts] = React.useState<
        Array<{
            name: string;
            amount: string;
            remark: string;
        }>
    >([]);

    const [nonRevolvingDebts, setNonRevolvingDebts] = React.useState<
        Array<{
            name: string;
            amount: string;
            remark: string;
        }>
    >([]);

    const [equityDebts, setEquityDebts] = React.useState<
        Array<{
            name: string;
            amount: string;
            remark: string;
        }>
    >([]);

    // Query to fetch debt details
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["debt", companyId],
        queryFn: () => getDebt(companyId as string, yearly),
    });

    // Mutation to edit debt details
    const editDebtMutation = useMutation({
        mutationFn: (request: any) => editDebt(companyId as string, request),
        onSuccess: () => {
            toast({
                title: "ສຳເລັດ",
                description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
                variant: "success",
            });
            queryClient.invalidateQueries({ queryKey: ["debt", companyId] });
            navigate({ to: "/report/finance/company/finance-debt" });
        },
        onError: (error) => {
            console.error("Error:", error);
            toast({
                title: "ບໍ່ສຳເລັດ",
                description: "ບັນທຶກຂໍ້ມູນບໍ່ສຳເລັດ",
                variant: "destructive",
            });
        },
    });

    // Function
    const formatNumber = (value: string | number) => {
        if (!value && value !== 0) return "";

        // Convert to string and remove existing formatting
        let stringValue = String(value).replace(/[,\s]/g, "");

        // Handle parentheses
        const isNegative =
            stringValue.startsWith("(") && stringValue.endsWith(")");
        if (isNegative) {
            stringValue = stringValue.slice(1, -1);
        }

        // Split into integer and decimal parts
        const [integerPart, decimalPart] = stringValue.split(".");

        // Format integer part with commas
        const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ","
        );

        // Combine with decimal part if it exists
        const formattedValue =
            decimalPart !== undefined
                ? `${formattedInteger}.${decimalPart.slice(0, 2)}`
                : formattedInteger;

        return isNegative ? `(${formattedValue})` : formattedValue;
    };

    // Effect to populate the form with current debt data
    useEffect(() => {
        if (data?.data) {
            setDocFile(data?.data?.docFile || "");

            const formatInitialAmount = (amount: number) => {
                if (amount < 0) {
                    return `(${Math.abs(amount)})`;
                }
                return String(amount);
            };

            const revolvingDebts =
                data?.data?.debtDetails?.filter(
                    (debt: any) => debt.debtType === "REVOLVING"
                ) || [];
            const nonRevolvingDebts =
                data?.data?.debtDetails?.filter(
                    (debt: any) => debt.debtType === "NON_REVOLVING"
                ) || [];
            const equityDebts =
                data?.data?.debtDetails?.filter(
                    (debt: any) => debt.debtType === "EQUITY"
                ) || [];

            setRevolvingDebts(
                revolvingDebts.map((debt: any) => ({
                    name: debt.name,
                    amount: formatInitialAmount(debt.amount),
                    remark: debt.remark,
                }))
            );

            setNonRevolvingDebts(
                nonRevolvingDebts.map((debt: any) => ({
                    name: debt.name,
                    amount: formatInitialAmount(debt.amount),
                    remark: debt.remark,
                }))
            );

            setEquityDebts(
                equityDebts.map((debt: any) => ({
                    name: debt.name,
                    amount: formatInitialAmount(debt.amount),
                    remark: debt.remark,
                }))
            );
        }
    }, [data]);

    if (isLoading) return <div>Loading...</div>;
    if (isError) return <div>Error: {error.message}</div>;

    // Function to handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!companyId) return;

        if (!docFile) {
            toast({
                title: "ບັນທຶກລົ້ມເຫຼວ",
                description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
                variant: "destructive",
            });
            return;
        }

        const convertAmount = (amount: string) => {
            if (!amount) return 0;
            const cleanValue = amount.replace(/[,()]/g, "");
            const numValue = Number(cleanValue);
            return amount.includes("(") ? -numValue : numValue;
        };

        const debtDetails = [
            ...equityDebts.map((debt) => ({
                ...debt,
                amount: convertAmount(debt.amount),
                debtType: "EQUITY" as const,
            })),
            ...revolvingDebts.map((debt) => ({
                ...debt,
                amount: convertAmount(debt.amount),
                debtType: "REVOLVING" as const,
            })),
            ...nonRevolvingDebts.map((debt) => ({
                ...debt,
                amount: convertAmount(debt.amount),
                debtType: "NON_REVOLVING" as const,
            })),
        ];

        const data = {
            companyId,
            yearly: yearly,
            docFile,
            debtDetails,
        };

        editDebtMutation.mutate(data);
    };

    // Sum calculation function
    const calculateSum = (debts: Array<{ amount: string }>) => {
        return debts.reduce((sum, debt) => {
            if (!debt.amount) return sum;

            const cleanValue = debt.amount.replace(/[,()]/g, "");
            const numValue = Number(cleanValue);

            if (isNaN(numValue)) return sum;

            // If the amount is in parentheses, treat it as negative
            return sum + (debt.amount.includes("(") ? -numValue : numValue);
        }, 0);
    };

    const equitySum = calculateSum(equityDebts);
    const revolvingSum = calculateSum(revolvingDebts);
    const nonRevolvingSum = calculateSum(nonRevolvingDebts);
    const totalSum = equitySum + revolvingSum + nonRevolvingSum;

    return (
        <section>
            <Card className="border-none">
                <CardHeader className="border-b mb-[32px]">
                    <button
                        type="button"
                        onClick={() =>
                            navigate({
                                to: "/report/finance/company/finance-debt",
                            })
                        }
                        className="flex items-center gap-[4px] mb-4"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        ກັບຄືນ
                    </button>
                    <CardTitle>ເພີ່ມຂໍ້ມູນໜີ້ສິນ</CardTitle>
                    <CardDescription>
                        ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        {/* Year Input */}
                        <div className="flex flex-col gap-2 mb-4">
                            <Label>ປະຈຳປີ</Label>
                            <div className="flex gap-x-2 items-center">
                                <Input
                                    type="number"
                                    value={yearly}
                                    placeholder="ປ້ອນປີ..."
                                    className="max-w-[300px]"
                                />
                            </div>
                        </div>

                        {/* Debt Table */}
                        <Table>
                            <TableHeader>
                                <TableRow className="[&>*]:font-bold [&>*]:text-black">
                                    <TableHead className="w-auto border">
                                        ຊັບສິນ
                                    </TableHead>
                                    <TableHead className="w-auto border">
                                        ມູນຄ່າຍັງເຫຼືອປີນີ້ {yearly}
                                    </TableHead>
                                    <TableHead className="w-auto border">
                                        ໝາຍເຫດ
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                                    <TableCell colSpan={4}>
                                        I ໜີ້ສິນໝູນວຽນ
                                    </TableCell>
                                    {/* <TableCell>
										<Button
											type="button"

											size="icon"
											onClick={() => addDebt("REVOLVING", setRevolvingDebts)}
										>
											<Plus className="w-4 h-4" />
										</Button>
									</TableCell> */}
                                </TableRow>
                                {revolvingDebts.map((debt, index) => (
                                    <TableRow
                                        key={index}
                                        className="[&>*]:border"
                                    >
                                        <TableCell className="flex gap-x-1 items-center">
                                            {index + 1}.
                                            <Input
                                                value={debt.name}
                                                onChange={(e) => {
                                                    const newDebts = [
                                                        ...revolvingDebts,
                                                    ];
                                                    newDebts[index].name =
                                                        e.target.value;
                                                    setRevolvingDebts(newDebts);
                                                }}
                                                readOnly
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                type="text"
                                                value={formatNumber(
                                                    debt.amount
                                                )}
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;
                                                    const newDebts = [
                                                        ...revolvingDebts,
                                                    ];

                                                    // Allow empty input
                                                    if (!value) {
                                                        newDebts[index].amount =
                                                            "";
                                                        setRevolvingDebts(
                                                            newDebts
                                                        );
                                                        return;
                                                    }

                                                    // Remove existing formatting
                                                    const rawValue =
                                                        value.replace(/,/g, "");

                                                    // Check if the input matches our pattern (including decimals and parentheses)
                                                    if (
                                                        /^[(]?\d*\.?\d*[)]?$/.test(
                                                            rawValue
                                                        )
                                                    ) {
                                                        newDebts[index].amount =
                                                            rawValue;
                                                        setRevolvingDebts(
                                                            newDebts
                                                        );
                                                    }
                                                }}
                                                className="text-right"
                                                inputMode="numeric"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                value={debt.remark}
                                                onChange={(e) => {
                                                    const newDebts = [
                                                        ...revolvingDebts,
                                                    ];
                                                    newDebts[index].remark =
                                                        e.target.value;
                                                    setRevolvingDebts(newDebts);
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {/* <Button
									type="button"

									className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
									size="icon"
									onClick={() => addDebt("REVOLVING", setRevolvingDebts)}
								>
									<CirclePlus className="w-4 h-4" />
									ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
								</Button> */}
                                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                    <TableCell>ໜີ້ສິນໝູນວຽນ I</TableCell>
                                    <TableCell className="text-right">
                                        {revolvingSum
                                            ? revolvingSum.toLocaleString()
                                            : ""}
                                    </TableCell>
                                    <TableCell colSpan={2}> </TableCell>
                                </TableRow>
                                {/* Non-Revolving Debts */}
                                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                                    <TableCell colSpan={4}>
                                        II ໜີ້ສິນບໍ່ໝູນວຽນ
                                    </TableCell>
                                    {/* <TableCell>
										<Button
											type="button"

											size="icon"
											onClick={() =>
												addDebt("NON_REVOLVING", setNonRevolvingDebts)
											}
										>
											<Plus className="w-4 h-4" />
										</Button>
									</TableCell> */}
                                </TableRow>
                                {nonRevolvingDebts.map((debt, index) => (
                                    <TableRow
                                        key={index}
                                        className="[&>*]:border"
                                    >
                                        <TableCell className="flex gap-x-1 items-center">
                                            {index + 1}.
                                            <Input
                                                value={debt.name}
                                                onChange={(e) => {
                                                    const newDebts = [
                                                        ...nonRevolvingDebts,
                                                    ];
                                                    newDebts[index].name =
                                                        e.target.value;
                                                    setNonRevolvingDebts(
                                                        newDebts
                                                    );
                                                }}
                                                readOnly
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {/* <Input
												className="text-right"
												type="text"
												value={formatNumber(debt.amount)}
												onChange={(e) => {
													const value = e.target.value.replace(/,/g, "");
													const numValue = value === "" ? 0 : Number(value);
													if (!isNaN(numValue)) {
														const newDebts = [...nonRevolvingDebts];
														newDebts[index].amount = numValue;
														setNonRevolvingDebts(newDebts);
													}
												}}
											/> */}

                                            <Input
                                                type="text"
                                                value={formatNumber(
                                                    debt.amount
                                                )}
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;
                                                    const newDebts = [
                                                        ...nonRevolvingDebts,
                                                    ];

                                                    // Allow empty input
                                                    if (!value) {
                                                        newDebts[index].amount =
                                                            "";
                                                        setNonRevolvingDebts(
                                                            newDebts
                                                        );
                                                        return;
                                                    }

                                                    // Remove existing formatting
                                                    const rawValue =
                                                        value.replace(/,/g, "");

                                                    // Check if the input matches our pattern (including decimals and parentheses)
                                                    if (
                                                        /^[(]?\d*\.?\d*[)]?$/.test(
                                                            rawValue
                                                        )
                                                    ) {
                                                        newDebts[index].amount =
                                                            rawValue;
                                                        setNonRevolvingDebts(
                                                            newDebts
                                                        );
                                                    }
                                                }}
                                                className="text-right"
                                                inputMode="numeric"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                value={debt.remark}
                                                onChange={(e) => {
                                                    const newDebts = [
                                                        ...nonRevolvingDebts,
                                                    ];
                                                    newDebts[index].remark =
                                                        e.target.value;
                                                    setNonRevolvingDebts(
                                                        newDebts
                                                    );
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {/* <Button
									type="button"

									className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
									size="icon"
									onClick={() => addDebt("NON_REVOLVING", setNonRevolvingDebts)}
								>
									<CirclePlus className="w-4 h-4" />
									ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
								</Button> */}
                                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                    <TableCell>ໜີ້ສິນບໍ່ໝູນວຽນ II</TableCell>
                                    <TableCell className="text-right">
                                        {nonRevolvingSum
                                            ? nonRevolvingSum.toLocaleString()
                                            : ""}
                                    </TableCell>
                                    <TableCell colSpan={2}> </TableCell>
                                </TableRow>

                                {/* Equity Debts */}
                                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                                    <TableCell colSpan={4}>
                                        III ທຶນຕົນເອງ
                                    </TableCell>
                                </TableRow>
                                {equityDebts.map((debt, index) => (
                                    <TableRow
                                        key={index}
                                        className="[&>*]:border"
                                    >
                                        <TableCell className="flex gap-x-1 items-center">
                                            {index + 1}.
                                            <Input
                                                value={debt.name}
                                                onChange={(e) => {
                                                    const newDebts = [
                                                        ...equityDebts,
                                                    ];
                                                    newDebts[index].name =
                                                        e.target.value;
                                                    setEquityDebts(newDebts);
                                                }}
                                                readOnly
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {/* <Input
												type="text"
												className="text-right"
												value={formatNumber(debt.amount)}
												onChange={(e) => {
													const value = e.target.value.replace(/,/g, "");
													const numValue = value === "" ? 0 : Number(value);
													if (!isNaN(numValue)) {
														const newDebts = [...equityDebts];
														newDebts[index].amount = numValue;
														setEquityDebts(newDebts);
													}
												}}
											/> */}
                                            <Input
                                                type="text"
                                                value={formatNumber(
                                                    debt.amount
                                                )}
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;
                                                    const newDebts = [
                                                        ...equityDebts,
                                                    ];

                                                    // Allow empty input
                                                    if (!value) {
                                                        newDebts[index].amount =
                                                            "";
                                                        setEquityDebts(
                                                            newDebts
                                                        );
                                                        return;
                                                    }

                                                    // Remove existing formatting
                                                    const rawValue =
                                                        value.replace(/,/g, "");

                                                    // Check if the input matches our pattern (including decimals and parentheses)
                                                    if (
                                                        /^[(]?\d*\.?\d*[)]?$/.test(
                                                            rawValue
                                                        )
                                                    ) {
                                                        newDebts[index].amount =
                                                            rawValue;
                                                        setEquityDebts(
                                                            newDebts
                                                        );
                                                    }
                                                }}
                                                className="text-right"
                                                inputMode="numeric"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                value={debt.remark}
                                                onChange={(e) => {
                                                    const newDebts = [
                                                        ...equityDebts,
                                                    ];
                                                    newDebts[index].remark =
                                                        e.target.value;
                                                    setEquityDebts(newDebts);
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {/* <Button
									type="button"

									className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
									size="icon"
									onClick={() => addDebt("EQUITY", setEquityDebts)}
								>
									<CirclePlus className="w-4 h-4" />
									ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
								</Button> */}
                                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                    <TableCell>ລວມທຶນຕົນເອງ (III)</TableCell>
                                    <TableCell className="text-right">
                                        {equitySum
                                            ? equitySum.toLocaleString()
                                            : ""}
                                    </TableCell>
                                    <TableCell colSpan={2}></TableCell>
                                </TableRow>
                                {/* Revolving Debts */}

                                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                    <TableCell>
                                        ລວມຍອດໜີ້ສິນທັງໝົດ ( I + II + III )
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {totalSum
                                            ? totalSum.toLocaleString()
                                            : ""}
                                    </TableCell>
                                    <TableCell colSpan={2}> </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>

                        {/* File Input */}
                        <div className="my-4 w-fit">
                            <FileUpload
                                currentFile={docFile}
                                onFileUpload={(file) => setDocFile(file)}
                            />
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex gap-x-2 justify-end mt-4">
                            <Button
                                type="button"
                                variant={"outline"}
                                onClick={() =>
                                    navigate({
                                        to: "/report/finance",
                                        search: {
                                            page: 1,
                                            startYear: 0,
                                            endYear: 0,
                                        },
                                    })
                                }
                            >
                                ຍົກເລີກ
                            </Button>
                            <Button
                                type="submit"
                                className="flex gap-x-2 items-center"
                            >
                                {editDebtMutation.isPending ? (
                                    <Spinner className="text-white" />
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        ບັນທຶກ
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </section>
    );
}
